/// BottomPanel: Resizable bottom panel with Console/Variables/Jobs/Tasks/Debug/Watch/Metrics/AI Chat tabs.
/// Uses Radix Tabs for tab switching and pointer events for top-edge resize.

import { useState, useCallback, useRef } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { ChevronDown, Sparkles } from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { useEngineStore } from "@/stores/engineStore";
import { useDebugStore } from "@/stores/debugStore";
import { useAIStore } from "@/stores/aiStore";
import { ConsolePanel } from "@/components/panels/ConsolePanel";
import { VariableInspector } from "@/components/panels/VariableInspector";
import { JobPanel } from "@/components/panels/JobPanel";
import { TaskPanel } from "@/components/panels/TaskPanel";
import { DebugPanel } from "@/components/panels/DebugPanel";
import { WatchPanel } from "@/components/panels/WatchPanel";
import { MetricsPanel } from "@/components/panels/MetricsPanel";
import { HeatmapControls } from "@/components/panels/HeatmapControls";
import { AIChatPanel } from "@/components/ai/AIChatPanel";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BottomPanel() {
  const visible = useUIStore((s) => s.bottomPanelVisible);
  const height = useUIStore((s) => s.bottomPanelHeight);
  const setHeight = useUIStore((s) => s.setBottomPanelHeight);
  const toggle = useUIStore((s) => s.toggleBottomPanel);
  const taskCount = useEngineStore((s) => s.userTasks.length);
  const debugMode = useDebugStore((s) => s.mode);
  const aiStreaming = useAIStore((s) => s.isStreaming);

  const [activeTab, setActiveTab] = useState("console");
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  // Resize via pointer drag on top edge
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startY: e.clientY, startHeight: height };
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
    },
    [height],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      // Dragging up increases height
      const delta = dragRef.current.startY - e.clientY;
      setHeight(dragRef.current.startHeight + delta);
    },
    [setHeight],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="bottom-panel"
      className="relative flex flex-col border-t border-zinc-700 bg-zinc-800 overflow-hidden select-none"
      style={{ height: `${height}px`, minHeight: "100px", maxHeight: "500px" }}
    >
      {/* Top-edge resize handle */}
      <div
        className="absolute left-0 right-0 top-0 h-1 cursor-row-resize hover:bg-blue-500/30 active:bg-blue-500/50 z-10"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />

      {/* Tab header */}
      <div className="flex items-center border-b border-zinc-700 px-2 pt-1">
        <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <Tabs.List className="flex gap-1">
            <TabTrigger value="console" active={activeTab === "console"}>
              Console
            </TabTrigger>
            <TabTrigger value="variables" active={activeTab === "variables"}>
              Variables
            </TabTrigger>
            <TabTrigger value="jobs" active={activeTab === "jobs"}>
              Jobs
            </TabTrigger>
            <TabTrigger value="tasks" active={activeTab === "tasks"}>
              Tasks
              {taskCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-[10px] text-white font-medium">
                  {taskCount}
                </span>
              )}
            </TabTrigger>
            <TabTrigger value="debug" active={activeTab === "debug"}>
              Debug
              {debugMode !== "idle" && (
                <span
                  className={`ml-1 inline-block h-2 w-2 rounded-full ${
                    debugMode === "running"
                      ? "bg-green-500"
                      : debugMode === "paused"
                        ? "bg-yellow-500"
                        : debugMode === "stepping"
                          ? "bg-red-500"
                          : "bg-zinc-500"
                  }`}
                />
              )}
            </TabTrigger>
            <TabTrigger value="watch" active={activeTab === "watch"}>
              Watch
            </TabTrigger>
            <TabTrigger value="metrics" active={activeTab === "metrics"}>
              Metrics
            </TabTrigger>
            <TabTrigger value="ai" active={activeTab === "ai"}>
              <Sparkles className="inline h-3 w-3 mr-1" />
              AI Chat
              {aiStreaming && (
                <span className="ml-1 inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              )}
            </TabTrigger>
          </Tabs.List>
        </Tabs.Root>

        <button
          type="button"
          className="ml-1 rounded p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={toggle}
          aria-label="Collapse bottom panel"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === "console" && <ConsolePanel />}
        {activeTab === "variables" && <VariableInspector />}
        {activeTab === "jobs" && <JobPanel />}
        {activeTab === "tasks" && <TaskPanel />}
        {activeTab === "debug" && (
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0 overflow-hidden">
              <DebugPanel />
            </div>
            <HeatmapControls />
          </div>
        )}
        {activeTab === "watch" && <WatchPanel />}
        {activeTab === "metrics" && <MetricsPanel />}
        {activeTab === "ai" && <AIChatPanel />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper sub-components
// ---------------------------------------------------------------------------

function TabTrigger({
  value,
  active,
  children,
}: {
  value: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tabs.Trigger
      value={value}
      className={`rounded-t px-3 py-1 text-xs transition-colors ${
        active
          ? "bg-zinc-900 text-zinc-100 border-b-2 border-blue-500"
          : "text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </Tabs.Trigger>
  );
}

